import { useMemo } from 'react';
import * as oauth from 'oauth4webapi';
import type { OAuthCallbackPayload } from '@domain/auth/types';
import type { AuthOAuthOption } from '../components/AuthScreen';
import {
  oauthAppleClientId,
  oauthAppleRedirectUri,
  oauthGoogleClientId,
  oauthTwitterClientId,
} from '@shared/config/runtime';

type LoginWithOAuth = (payload: OAuthCallbackPayload) => Promise<unknown>;

type AppleSignInResponse = {
  authorization?: {
    id_token?: string;
    code?: string;
  };
  user?: {
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
};

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (options: Record<string, unknown>) => void;
        signIn: () => Promise<AppleSignInResponse>;
      };
    };
  }
}

const appleScriptId = 'locs-apple-signin-js';

function loadAppleScript() {
  if (window.AppleID?.auth) {
    return Promise.resolve();
  }

  const existing = document.getElementById(appleScriptId) as HTMLScriptElement | null;
  if (existing) {
    return new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Apple 登录组件加载失败')), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = appleScriptId;
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Apple 登录组件加载失败'));
    document.head.appendChild(script);
  });
}

function splitAppleName(response: AppleSignInResponse) {
  const name = response.user?.name;
  return { firstName: name?.firstName || undefined, lastName: name?.lastName || undefined };
}

function waitForTwitterCallback(expectedState: string) {
  return new Promise<{ code: string }>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('X 登录等待超时，请重试'));
    }, 120000);

    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data as {
        type?: string;
        code?: string | null;
        state?: string | null;
        error?: string | null;
      };
      if (data?.type !== 'LOCS_OAUTH_TWITTER_CALLBACK') {
        return;
      }

      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);

      if (data.error) {
        reject(new Error('X 登录已取消或失败'));
        return;
      }
      if (!data.code || data.state !== expectedState) {
        reject(new Error('X 登录校验失败，请重新尝试'));
        return;
      }
      resolve({ code: data.code });
    }

    window.addEventListener('message', onMessage);
  });
}

function waitForGoogleCallback(expectedState: string) {
  return new Promise<{ idToken: string }>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Google 登录等待超时，请重试'));
    }, 120000);
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; idToken?: string | null; state?: string | null; error?: string | null };
      if (data?.type !== 'LOCS_OAUTH_GOOGLE_CALLBACK') return;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      if (data.error || !data.idToken || data.state !== expectedState) {
        reject(new Error('Google 登录校验失败，请重新尝试'));
        return;
      }
      resolve({ idToken: data.idToken });
    }
    window.addEventListener('message', onMessage);
  });
}

export function useOAuthLoginOptions(loginWithOAuth: LoginWithOAuth): AuthOAuthOption[] {
  const googleNonce = useMemo(() => crypto.randomUUID(), []);
  const appleNonce = useMemo(() => crypto.randomUUID(), []);
  const handleGoogleLogin = async () => {
    if (!oauthGoogleClientId) throw new Error('Google 登录尚未配置');
    const state = oauth.generateRandomState();
    const redirectUri = `${window.location.origin}/auth/callback/google`;
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', oauthGoogleClientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'id_token');
    authUrl.searchParams.set('scope', 'openid email profile');
    authUrl.searchParams.set('nonce', googleNonce);
    authUrl.searchParams.set('state', state);
    const callbackPromise = waitForGoogleCallback(state);
    const popup = window.open(authUrl.toString(), 'locs-google-login', 'width=560,height=720');
    if (!popup) throw new Error('浏览器阻止了 Google 登录窗口');
    const { idToken } = await callbackPromise;
    await loginWithOAuth({ provider: 'google', idToken, nonce: googleNonce });
  };

  return useMemo(() => {
    const handleAppleLogin = async () => {
      if (!oauthAppleClientId) {
        throw new Error('Apple 登录尚未配置');
      }
      await loadAppleScript();
      const redirectURI = oauthAppleRedirectUri || `${window.location.origin}/auth/callback/apple`;
      window.AppleID?.auth.init({
        clientId: oauthAppleClientId,
        scope: 'name email',
        redirectURI,
        usePopup: true,
        nonce: appleNonce,
      });
      const response = await window.AppleID?.auth.signIn();
      const identityToken = response?.authorization?.id_token;
      if (!identityToken) {
        throw new Error('Apple 登录没有返回身份凭证');
      }
      await loginWithOAuth({
        provider: 'apple',
        identityToken,
        authorizationCode: response?.authorization?.code || '',
        nonce: appleNonce,
        ...splitAppleName(response),
      });
    };

    const handleTwitterLogin = async () => {
      if (!oauthTwitterClientId) {
        throw new Error('X 登录尚未配置');
      }
      const issuer = new URL('https://twitter.com');
      const redirectUri = `${window.location.origin}/auth/callback/twitter`;
      const codeVerifier = oauth.generateRandomCodeVerifier();
      const codeChallenge = await oauth.calculatePKCECodeChallenge(codeVerifier);
      const state = oauth.generateRandomState();
      const authUrl = new URL('/i/oauth2/authorize', issuer);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('client_id', oauthTwitterClientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('scope', 'tweet.read users.read offline.access');
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');

      const callbackPromise = waitForTwitterCallback(state);
      const popup = window.open(authUrl.toString(), 'locs-twitter-login', 'width=560,height=720');
      if (!popup) {
        throw new Error('浏览器阻止了 X 登录窗口');
      }

      const { code } = await callbackPromise;
      await loginWithOAuth({
        provider: 'twitter',
        authorizationCode: code,
        codeVerifier,
        redirectUri,
        state,
      });
    };

    return [
      {
        provider: 'google',
        label: 'Google',
        onClick: () => void handleGoogleLogin(),
      },
      {
        provider: 'apple',
        label: 'Apple',
        onClick: () => void handleAppleLogin(),
      },
      {
        provider: 'twitter',
        label: 'X',
        onClick: () => void handleTwitterLogin(),
      },
    ].filter((option) => {
      if (option.provider === 'google') return Boolean(oauthGoogleClientId);
      if (option.provider === 'apple') return Boolean(oauthAppleClientId);
      return Boolean(oauthTwitterClientId);
    }) as AuthOAuthOption[];
  }, [appleNonce, googleNonce, loginWithOAuth]);
}
